import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  nextTick,
  defineAsyncComponent,
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from 'vue'
import { useI18n } from 'vue-i18n'
import { PontotelBotaoIcone } from '@Lar21/imaginii-web-components/lib/vue'
import { observarVisibilidadeDoCrisp } from '@/typescript/servicos/crisp/visibilidadeDoCrisp'
import fotoNina from '@/../static/images/nina.png'
import {
  CapacidadeAssistente,
  ContextoDeAtuacao,
  EscolhaDaSelecaoDaAntecipacao,
  MensagemAssistente,
  OpcoesAvancadas as OpcoesAvancadasDaConversa,
  PreviaDeMutacao,
  RespostaDaSolicitacaoDeParametros,
  ResultadoDeTurno,
  VotoDaAvaliacao
} from '../../dominio/assistente.interface'
import OpcoesAvancadas from './OpcoesAvancadas.vue'
import type { ModoDeAcao } from './opcoesAvancadas'
import { observarTela, registrarColheitaDeContextoDeTela } from '../canvas/observadorDeTela'
import {
  formaDoAcoplamento,
  lerIntencaoDoAcoplamento,
  persistirIntencaoDoAcoplamento,
  proximaIntencao
} from './acoplamentoDoAssistente'
import type {
  FormaDoAcoplamento,
  GestoDoAcoplamento,
  IntencaoDoAcoplamento
} from './acoplamentoDoAssistente'

const SELETOR_DE_EXCLUSAO_DA_COLHEITA = '[data-assistente-ignorar]'


export default /*@__PURE__*/_defineComponent({
  __name: 'AssistenteDock',
  props: {
    montado: { type: Boolean, required: false },
    intencao: { type: String, required: false },
    larguraDaJanela: { type: Number, required: false },
    messages: { type: Array, required: false },
    resultadosPorId: { type: Object, required: false },
    resultadoAtivoId: { type: [String, null], required: false },
    resultadoVisivel: { type: Boolean, required: false },
    conversaEmAbas: { type: Boolean, required: false },
    conversaOculta: { type: Boolean, required: false },
    context: { type: Object, required: false },
    loading: { type: Boolean, required: false },
    leituraDaTelaAutorizada: { type: Boolean, required: false },
    opcoesAvancadas: { type: Object, required: false }
  },
  emits: ["send", "update:intencao", "nova-conversa", "preview", "abrir-resultado", "abrir-navegacao", "aceitar-oferta-de-suporte", "selecionar-opcao", "responder-solicitacao-de-parametros", "avaliar-resposta", "executar-planejamento", "history", "politicas", "update:modo-de-acao"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props

const emit = __emit

const { t } = useI18n()
const suporteVisivel = ref(false)
let pararDeObservarSuporte: (() => void) | undefined

const contextoPadrao: ContextoDeAtuacao = {
  userId: 'assistente-dock',
  displayName: 'Usuário Pontotel',
  role: 'gestor',
  companyId: 'empresa-pontotel',
  companyName: 'Pontotel',
  scope: {}
}

function colherTelaHospedeira () {
  return observarTela(window, { ignorar: SELETOR_DE_EXCLUSAO_DA_COLHEITA })
}

const intencaoLocal = ref<IntencaoDoAcoplamento>(
  props.intencao ?? lerIntencaoDoAcoplamento()
)
const perguntaRecolhida = ref('')

function intencaoValida (valor: unknown): valor is IntencaoDoAcoplamento {
  return valor === 'minimizado' || valor === 'recolhido' || valor === 'aberto'
}

watch(() => props.intencao, (novaIntencao) => {
  if (intencaoValida(novaIntencao)) intencaoLocal.value = novaIntencao
})

const larguraDaJanela = ref(
  typeof props.larguraDaJanela === 'number'
    ? props.larguraDaJanela
    : typeof window === 'undefined' ? 0 : window.innerWidth
)

function atualizarLarguraDaJanela (): void {
  if (typeof props.larguraDaJanela === 'number') {
    larguraDaJanela.value = props.larguraDaJanela
    return
  }
  if (typeof window !== 'undefined') larguraDaJanela.value = window.innerWidth
}

watch(() => props.larguraDaJanela, atualizarLarguraDaJanela)

const forma = computed<FormaDoAcoplamento>(() => formaDoAcoplamento({
  intencao: intencaoLocal.value,
  larguraDaJanela: larguraDaJanela.value,
  montado: props.montado !== false,
  resultadoAberto: props.resultadoVisivel === true
}))

const painelDaConversa = ref<HTMLElement | null>(null)
const conversaMontada = ref(false)
watch(forma, async (atual, anterior) => {
  if (atual !== 'acoplado' && atual !== 'sobreposto') return
  conversaMontada.value = true
  if (anterior !== undefined && anterior !== 'acoplado' && anterior !== 'sobreposto') {
    await nextTick()
    painelDaConversa.value?.focus()
  }
}, { immediate: true })

const mensagens = computed(() => props.messages ?? [])
const selecaoPendente = computed(() => mensagens.value.some(mensagem => (
  mensagem.selecao?.estado === 'pendente' ||
  mensagem.selecao?.estado === 'enviando' ||
  mensagem.selecao?.estado === 'falhou'
)))
const contexto = computed(() => props.context ?? contextoPadrao)
// Carrega a conversa quando a pessoa abre o chat.
const ConversationPane = defineAsyncComponent({
  loader: () => import('../conversa/ConversationPane.vue'),
  errorComponent: defineComponent({
    name: 'ConversationPaneDegradado',
    setup: () => () => h('section', { class: 'conversa' })
  })
})

function mudarIntencao (gesto: GestoDoAcoplamento): void {
  const novaIntencao = proximaIntencao(intencaoLocal.value, gesto)
  if (novaIntencao === intencaoLocal.value) return
  intencaoLocal.value = novaIntencao
  persistirIntencaoDoAcoplamento(novaIntencao)
  emit('update:intencao', novaIntencao)
}

function despacharEnvio (texto: string): void {
  const pergunta = texto.trim()
  if (!pergunta || selecaoPendente.value) return
  emit('send', pergunta)
}

function enviarPerguntaRecolhida (): void {
  const pergunta = perguntaRecolhida.value.trim()
  if (!pergunta || props.loading === true || selecaoPendente.value) return
  despacharEnvio(pergunta)
  perguntaRecolhida.value = ''
  mudarIntencao('enviar')
}

function aoPressionarEscape (): void {
  if (forma.value === 'sobreposto') mudarIntencao('escapar')
}

function aoRedimensionar (): void {
  atualizarLarguraDaJanela()
  // Recalcular a forma nunca despacha uma transição de intenção.
  mudarIntencao('redimensionar')
}

const anuncioDaForma = computed(() => {
  if (forma.value !== 'acoplado' && forma.value !== 'sobreposto') return ''
  return t('assistentePontotel.canvas.titulo')
})

onMounted(() => {
  atualizarLarguraDaJanela()
  pararDeObservarSuporte = observarVisibilidadeDoCrisp(visivel => { suporteVisivel.value = visivel })
  window.addEventListener('resize', aoRedimensionar)
  registrarColheitaDeContextoDeTela(colherTelaHospedeira)
})

onBeforeUnmount(() => {
  pararDeObservarSuporte?.()
  window.removeEventListener('resize', aoRedimensionar)
  registrarColheitaDeContextoDeTela(null)
})

const __returned__ = { props, emit, t, suporteVisivel, get pararDeObservarSuporte() { return pararDeObservarSuporte }, set pararDeObservarSuporte(v) { pararDeObservarSuporte = v }, contextoPadrao, SELETOR_DE_EXCLUSAO_DA_COLHEITA, colherTelaHospedeira, intencaoLocal, perguntaRecolhida, intencaoValida, larguraDaJanela, atualizarLarguraDaJanela, forma, painelDaConversa, conversaMontada, mensagens, selecaoPendente, contexto, ConversationPane, mudarIntencao, despacharEnvio, enviarPerguntaRecolhida, aoPressionarEscape, aoRedimensionar, anuncioDaForma, get PontotelBotaoIcone() { return PontotelBotaoIcone }, get fotoNina() { return fotoNina }, OpcoesAvancadas }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})